import React, {useState, useRef, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import Swatch from '../../../swatch-group/swatch'
import SwatchGroup from '../../../swatch-group'
import {
    Box,
    Text,
    Button,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useBrand} from '../../../../contexts/brand-context'

const UnselectedVariationGroups = ({
    variationAttributes = [],
    setFindInStoreModalTitle,
    setIsVariantSelected
}) => {
    const styles = useMultiStyleConfig('UnselectedVariationGroups', {})
    const intl = useIntl()
    const initialMissingIdsRef = useRef(null)
    const {isGarage} = useBrand()

    if (initialMissingIdsRef.current === null) {
        initialMissingIdsRef.current = variationAttributes
            .filter(({selectedValue}) => !selectedValue?.value)
            .map(({id}) => id)
    }

    const initialMissingIds = initialMissingIdsRef.current
    const [isButtonDisabled, setIsButtonDisabled] = useState(true)

    useEffect(() => {
        const stillMissing = initialMissingIds.some((id) => {
            const group = variationAttributes.find((attr) => attr.id === id)
            return !group?.selectedValue?.value
        })
        setIsButtonDisabled(stillMissing)
    }, [variationAttributes, initialMissingIds])

    useEffect(() => {
        let modalTitle = isGarage
            ? intl.formatMessage(
                  {
                      id: 'pdp_find_in_store.select.attr.title.garage',
                      defaultMessage: 'select {missingAttr}'
                  },
                  {missingAttr: initialMissingIdsRef.current}
              )
            : intl.formatMessage(
                  {
                      id: 'pdp_find_in_store.select.attr.title.dynamite',
                      defaultMessage: 'select {missingAttr}'
                  },
                  {missingAttr: initialMissingIdsRef.current}
              )

        if (initialMissingIds.includes('length') > 0 && initialMissingIds.includes('size') > 0) {
            modalTitle = isGarage
                ? intl.formatMessage(
                      {
                          id: 'pdp_find_in_store.select.attr.title.with.length.garage',
                          defaultMessage: 'Select {size} and {length}'
                      },
                      {size: initialMissingIds[0], length: initialMissingIds[1]}
                  )
                : intl.formatMessage(
                      {
                          id: 'pdp_find_in_store.select.attr.title.with.length.dynamite',
                          defaultMessage: 'Select {size} and {length}'
                      },
                      {size: initialMissingIds[0], length: initialMissingIds[1]}
                  )
        }
        setFindInStoreModalTitle(modalTitle)
    }, [])

    return (
        <Box {...styles.container}>
            <Box {...styles.content}>
                <Text {...styles.description}>
                    {initialMissingIds.includes('length') > 0 &&
                    initialMissingIds.includes('size') > 0 ? (
                        isGarage ? (
                            <FormattedMessage
                                defaultMessage="Select a {size} and {length} to check availability in stores."
                                id="pdp_find_in_store.select.attr.text.width.length.garage"
                                values={{size: initialMissingIds[0], length: initialMissingIds[1]}}
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Select a {size} and {length} to check availability in stores."
                                id="pdp_find_in_store.select.attr.text.width.length.dynamite"
                                values={{size: initialMissingIds[0], length: initialMissingIds[1]}}
                            />
                        )
                    ) : isGarage ? (
                        <FormattedMessage
                            defaultMessage="Select one {missingAttr} to check availability in stores"
                            id="pdp_find_in_store.select.attr.text.garage"
                            values={{missingAttr: initialMissingIdsRef.current}}
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Select one {missingAttr} to check availability in stores"
                            id="pdp_find_in_store.select.attr.text.dynamite"
                            values={{missingAttr: initialMissingIdsRef.current}}
                        />
                    )}
                </Text>

                <Box {...styles.swatchesWrapper}>
                    {variationAttributes
                        .filter(({id}) => initialMissingIds.includes(id))
                        .map(({id, name, values, selectedValue}, _, filtered) => (
                            <SwatchGroup
                                key={id}
                                value={selectedValue?.value || ''}
                                displayName={name}
                                label={filtered.length > 1 ? name : undefined}
                                isPDP
                                isSizeSwatch={id !== 'color'}
                                id={id}
                            >
                                {values.map(({href, name, image, value, orderable}, index) => {
                                    const content = image ? (
                                        <Box
                                            {...styles.swatchImage}
                                            backgroundColor={name.toLowerCase()}
                                            backgroundImage={`url(${image.disBaseLink || image.link})`}
                                        />
                                    ) : (
                                        name
                                    )

                                    return (
                                        <Swatch
                                            isPDP
                                            isFindInStore
                                            key={value}
                                            href={href}
                                            disabled={!orderable}
                                            value={value}
                                            name={name}
                                            variant={id === 'color' ? 'circle' : 'square'}
                                            selected={selectedValue?.value === value}
                                            isFocusable={index === 0}
                                            id={id}
                                        >
                                            {content}
                                        </Swatch>
                                    )
                                })}
                            </SwatchGroup>
                        ))}
                </Box>
            </Box>

            <Box {...styles.footer}>
                <Button
                    variant="webPrimary"
                    {...styles.continueButton}
                    isDisabled={isButtonDisabled}
                    onClick={() => {
                        setIsVariantSelected(true)
                    }}
                >
                    <FormattedMessage
                        defaultMessage="Continue"
                        id="pdp_find_in_store.button.continue"
                    />
                </Button>
            </Box>
        </Box>
    )
}

UnselectedVariationGroups.propTypes = {
    variationAttributes: PropTypes.array,
    setFindInStoreModalTitle: PropTypes.func,
    setIsVariantSelected: PropTypes.func
}

export default UnselectedVariationGroups
